import { AppError, CriticalError } from '@package/services/src/Errors';
import { useEffect } from 'react';
import {
  useMutation,
  useQuery,
  QueryKey,
  QueryFunction,
  UseQueryResult,
  UseQueryOptions,
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
} from 'react-query';

export { useQueryClient } from 'react-query';

export type { UseQueryOptions } from 'react-query';

const isCriticalError = (err: unknown) => err && (!(err instanceof AppError) || err instanceof CriticalError);

const useCriticalErrorCheck = (err: unknown) => {
  useEffect(() => {
    if (isCriticalError(err)) {
      throw err;
    }
  }, [err]);
};

export const useStrictMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const mutationResult = useMutation<TData, TError, TVariables, TContext>(mutationFn, options);

  useCriticalErrorCheck(mutationResult.error);

  return mutationResult;
};

export const useStrictQuery = <TResult = unknown, TError = unknown>(
  queryKey: QueryKey,
  queryFn: QueryFunction<TResult>,
  config?: UseQueryOptions<TResult, TError>,
): UseQueryResult<TResult, TError> => {
  const queryResult = useQuery<TResult, TError>({ queryKey, queryFn, ...config });

  useCriticalErrorCheck(queryResult.error);

  return queryResult;
};
